import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, X, Check } from 'lucide-react';
import { useApp, MAX_SESSIONS } from '@/context/AppContext';
import { formatUZS } from '@/lib/format';
import { Input } from '@/components/ui/input';

export default function CartSessionTabs() {
  const { t } = useTranslation();
  const { sessions, activeSessionId, createSession, switchSession, closeSession, renameSession, sessionTotalOf } = useApp();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draftName, setDraftName] = useState('');

  const startRename = (id: string, current: string) => {
    setEditingId(id);
    setDraftName(current);
  };

  const commitRename = () => {
    if (editingId) renameSession(editingId, draftName);
    setEditingId(null);
  };

  return (
    <div className="flex items-center gap-1.5 px-2 py-2 border-b border-border overflow-x-auto">
      {sessions.map((s, i) => {
        const isActive = s.id === activeSessionId;
        if (editingId === s.id) {
          return (
            <div key={s.id} className="flex items-center gap-1 shrink-0">
              <Input
                value={draftName}
                onChange={e => setDraftName(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') commitRename(); if (e.key === 'Escape') setEditingId(null); }}
                className="h-8 w-28 text-xs"
                autoFocus
              />
              <button onClick={commitRename} className="p-1.5 rounded-md text-success hover:bg-success/10">
                <Check size={14} />
              </button>
            </div>
          );
        }
        return (
          <button
            key={s.id}
            onClick={() => switchSession(s.id)}
            onDoubleClick={() => startRename(s.id, s.name)}
            title={t('pos_tabs.rename_hint')}
            className={`group shrink-0 flex items-center gap-2 pl-3 pr-2 py-1.5 rounded-lg border text-left transition-colors ${
              isActive ? 'bg-primary text-primary-foreground border-primary' : 'bg-muted text-foreground border-transparent hover:bg-accent'
            }`}
          >
            <span className="flex flex-col">
              <span className="text-xs font-medium leading-tight max-w-[110px] truncate">{s.name}</span>
              <span className={`text-[10px] leading-tight ${isActive ? 'text-primary-foreground/80' : 'text-muted-foreground'}`}>
                {t('pos_common.count_pcs', { n: s.items.length })} · {formatUZS(sessionTotalOf(s.id))}
              </span>
            </span>
            {sessions.length > 1 && (
              <span
                role="button"
                aria-label={t('pos_tabs.close_aria')}
                onClick={e => {
                  e.stopPropagation();
                  if (s.items.length === 0 || window.confirm(t('pos_tabs.confirm_close', { name: s.name }))) {
                    closeSession(s.id);
                  }
                }}
                className={`p-0.5 rounded ${isActive ? 'hover:bg-primary-foreground/20' : 'hover:bg-destructive/10 hover:text-destructive'}`}
              >
                <X size={12} />
              </span>
            )}
            {i === 0 && null}
          </button>
        );
      })}

      {sessions.length < MAX_SESSIONS && (
        <button
          onClick={() => createSession()}
          title={t('pos_tabs.new')}
          className="shrink-0 w-8 h-8 rounded-lg border border-dashed border-border flex items-center justify-center text-muted-foreground hover:text-primary hover:border-primary transition-colors"
        >
          <Plus size={14} />
        </button>
      )}
    </div>
  );
}
